import {
  LSPClient,
  DEFAULT_CONFIG,
  SymbolKind,
  SymbolKindType,
  LSPUserConfig,
} from "./utils/lsp_core";
import {
  detectDominantLanguages,
  getLanguageFileExtensions,
} from "./utils/os_utils";
import * as path from "path";
import * as fs from "fs";
import { glob } from "glob";
import { semanticSearch, SemanticSearchResult } from "./semantic_search";
import { toFileUri } from "./utils/file_utils";

export interface SearchResult {
  type:
    | "file"
    | "function"
    | "class"
    | "interface"
    | "method"
    | "property"
    | "content"
    | "semantic";
  name: string;
  file: string;
  line: number;
  column: number;
  description?: string;
  kind: SymbolKindType;
  content?: string; // 匹配的文本内容
  context?: string; // 上下文（前后几行）
  relevance?: number; // 语义搜索的相关性分数
  keywords?: string[]; // 语义搜索的关键词
}

export interface SearchOptions {
  type?:
    | "file"
    | "function"
    | "class"
    | "interface"
    | "method"
    | "property"
    | "content"
    | "semantic"
    | "all";
  limit?: number;
  caseSensitive?: boolean;
  regex?: boolean;
  contextLines?: number; // 显示匹配行前后的行数
  semantic?: boolean; // 是否启用语义搜索
  minRelevance?: number; // 语义搜索的最小相关性分数
  file?: string; // 限制搜索到指定文件
  lspConfig?: LSPUserConfig; // 自定义 LSP 配置
}

export async function searchProject(
  query: string,
  projectRoot: string,
  options: SearchOptions = {},
): Promise<SearchResult[]> {
  const {
    type = "all",
    limit = 20,
    caseSensitive = false,
    regex = false,
    contextLines = 2,
    semantic = false,
    minRelevance = 0.1,
    file,
  } = options;

  const results: SearchResult[] = [];

  // 1. 语义搜索（优先级最高）
  if (semantic || type === "semantic") {
    const semanticResults = await semanticSearch(query, projectRoot, {
      limit: Math.floor(limit / 2),
      minRelevance,
      file,
      lspConfig: options.lspConfig,
    });

    // 转换语义搜索结果格式
    const convertedResults: SearchResult[] = semanticResults.map((result) => ({
      type: "semantic",
      name: result.name,
      file: result.file,
      line: result.line,
      column: result.column,
      description: result.description,
      kind: result.kind,
      context: result.context,
      relevance: result.relevance,
      keywords: result.keywords,
    }));

    results.push(...convertedResults);
  }

  // 2. 文件搜索
  if (type === "all" || type === "file") {
    const fileResults = await searchFiles(query, projectRoot, file);
    results.push(...fileResults);
  }

  // 3. 符号搜索（函数、类、接口等）
  if (
    type === "all" ||
    (type !== "file" && type !== "content" && type !== "semantic")
  ) {
    const symbolResults = await searchSymbols(query, projectRoot, type, file);
    results.push(...symbolResults);
  }

  // 4. 内容搜索（类似 grep）
  // 当查询为空且类型为 all 时，跳过内容搜索以提供更好的用户体验
  if ((type === "all" && query.trim() !== "") || type === "content") {
    const contentResults = await searchContent(query, projectRoot, {
      caseSensitive,
      regex,
      contextLines,
      file,
    });
    results.push(...contentResults);
  }

  // 5. 排序和限制结果
  return results
    .sort((a, b) => {
      // 优先显示语义搜索结果（按相关性排序）
      if (a.type === "semantic" && b.type === "semantic") {
        return (b.relevance || 0) - (a.relevance || 0);
      }
      if (a.type === "semantic") return -1;
      if (b.type === "semantic") return 1;

      // 优先显示精确匹配
      const aExact = a.name.toLowerCase() === query.toLowerCase();
      const bExact = b.name.toLowerCase() === query.toLowerCase();
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;

      // 然后按类型排序
      const typeOrder = {
        file: 0,
        function: 1,
        class: 2,
        interface: 3,
        method: 4,
        property: 5,
        content: 6,
        semantic: 7,
      };
      return typeOrder[a.type] - typeOrder[b.type];
    })
    .slice(0, limit);
}

async function searchFiles(
  query: string,
  projectRoot: string,
  targetFile?: string,
): Promise<SearchResult[]> {
  const results: SearchResult[] = [];

  try {
    let files: string[];

    if (targetFile) {
      // 如果指定了文件，只搜索该文件
      const targetPath = path.resolve(projectRoot, targetFile);
      if (fs.existsSync(targetPath)) {
        files = [targetPath];
      } else {
        return results;
      }
    } else {
      // 搜索所有文件
      files =
        (await glob("**/*", {
          cwd: projectRoot,
          ignore: ["node_modules/**", "dist/**", ".git/**"],
          absolute: true,
        })) ?? [];
    }

    const queryLower = query.toLowerCase();

    for (const file of files) {
      const relativePath = path.relative(projectRoot, file);
      const fileName = path.basename(file);

      // 检查文件名是否匹配
      if (
        fileName.toLowerCase().includes(queryLower) ||
        relativePath.toLowerCase().includes(queryLower)
      ) {
        const stats = fs.statSync(file);
        if (stats && stats.isFile()) {
          results.push({
            type: "file",
            name: fileName,
            file: relativePath,
            line: 1,
            column: 1,
            kind: SymbolKind.File,
          });
        }
      }
    }
  } catch (error) {
    console.error("文件搜索错误:", error);
  }

  return results;
}

async function searchSymbols(
  query: string,
  projectRoot: string,
  type: string,
  targetFile?: string,
): Promise<SearchResult[]> {
  const results: SearchResult[] = [];

  try {
    let lang: string;
    let file_ext: string;

    if (targetFile) {
      // 如果指定了文件，根据文件扩展名确定语言
      const ext = path.extname(targetFile).toLowerCase();
      const extToLang: Record<string, string> = {
        ".c": "c",
        ".h": "c",
        ".cpp": "cpp",
        ".hpp": "cpp",
        ".cc": "cpp",
        ".cxx": "cpp",
        ".py": "python",
        ".ts": "typescript",
        ".tsx": "typescript",
        ".js": "typescript",
        ".jsx": "typescript",
      };

      lang = extToLang[ext] || "typescript";
      file_ext = getLanguageFileExtensions(lang);
    } else {
      // 如果没有指定文件，使用项目主导语言
      const languages =
        detectDominantLanguages(projectRoot, {
          ignoredDirs: new Set(["node_modules", "dist"]),
          topN: 1,
        }) ?? [];

      if (languages.length === 0) {
        return results;
      }

      lang = languages[0];
      file_ext = getLanguageFileExtensions(lang);
    }

    const client = new LSPClient(toFileUri(projectRoot), {
      ...DEFAULT_CONFIG,
      timeout: 30000,
      logLevel: "error", // 减少日志输出
    });

    await client.start(lang);

    try {
      let files: string[];

      if (targetFile) {
        // 如果指定了文件，只搜索该文件
        const targetPath = path.resolve(projectRoot, targetFile);
        if (fs.existsSync(targetPath)) {
          files = [targetPath];
        } else {
          return results;
        }
      } else {
        files =
          (await glob(`**/*.{${file_ext}}`, {
            cwd: projectRoot,
            ignore: "node_modules/**",
            absolute: true,
          })) ?? [];
      }

      const queryLower = query.toLowerCase();

      for (const filePath of files) {
        const fileUri = toFileUri(filePath);
        const relativePath = path.relative(projectRoot, filePath);

        try {
          const content = fs.readFileSync(filePath, "utf-8");
          await client.sendDidOpen(fileUri, content, "typescript");

          const symbols = await client.sendDocumentSymbol(fileUri);
          if (!symbols) continue;

          for (const symbol of symbols) {
            if (shouldIncludeSymbol(symbol, type, queryLower)) {
              results.push({
                type: getSymbolType(symbol.kind),
                name: symbol.name,
                file: relativePath,
                line: symbol.location.range.start.line + 1,
                column: symbol.location.range.start.character + 1,
                description: symbol.detail || undefined,
                kind: symbol.kind,
              });
            }
          }

          await client.sendDidClose(fileUri);
        } catch (err) {
          // 忽略单个文件的错误，继续处理其他文件
        }
      }
    } finally {
      await client.shutdown();
    }
  } catch (error) {
    console.error("符号搜索错误:", error);
  }

  return results;
}

function shouldIncludeSymbol(
  symbol: any,
  type: string,
  query: string,
): boolean {
  // 检查类型过滤
  if (type !== "all") {
    const symbolType = getSymbolType(symbol.kind);
    if (symbolType !== type) {
      return false;
    }
  }

  // 检查名称匹配
  return symbol.name.toLowerCase().includes(query);
}

function getSymbolType(kind: SymbolKindType): SearchResult["type"] {
  switch (kind) {
    case SymbolKind.Function:
      return "function";
    case SymbolKind.Class:
      return "class";
    case SymbolKind.Interface:
      return "interface";
    case SymbolKind.Method:
      return "method";
    case SymbolKind.Property:
      return "property";
    default:
      return "function";
  }
}

async function searchContent(
  query: string,
  projectRoot: string,
  options: {
    caseSensitive: boolean;
    regex: boolean;
    contextLines: number;
    file?: string;
  },
): Promise<SearchResult[]> {
  const results: SearchResult[] = [];
  const { caseSensitive, regex, contextLines, file: targetFile } = options;

  try {
    // 搜索所有文本文件
    const textExtensions = [
      "ts",
      "js",
      "tsx",
      "jsx",
      "json",
      "md",
      "txt",
      "yml",
      "yaml",
      "xml",
      "html",
      "css",
      "scss",
      "less",
      "py",
      "java",
      "cpp",
      "c",
      "h",
      "hpp",
      "go",
      "rs",
      "php",
      "rb",
      "sh",
      "bash",
      "zsh",
      "fish",
    ];

    let files: string[];

    if (targetFile) {
      // 如果指定了文件，只搜索该文件
      const targetPath = path.resolve(projectRoot, targetFile);
      if (
        fs.existsSync(targetPath) &&
        textExtensions.some((ext) => targetPath.endsWith(`.${ext}`))
      ) {
        files = [targetPath];
      } else {
        return results;
      }
    } else {
      files =
        (await glob(`**/*.{${textExtensions.join(",")}}`, {
          cwd: projectRoot,
          ignore: ["node_modules/**", "dist/**", ".git/**"],
          absolute: true,
        })) ?? [];
    }

    const searchPattern = regex
      ? new RegExp(query, caseSensitive ? "g" : "gi")
      : new RegExp(escapeRegExp(query), caseSensitive ? "g" : "gi");

    for (const filePath of files) {
      try {
        const content = fs.readFileSync(filePath, "utf-8");
        const lines = content.split("\n");
        const relativePath = path.relative(projectRoot, filePath);

        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          const matches = line.match(searchPattern);

          if (matches) {
            // 构建上下文
            const startLine = Math.max(0, i - contextLines);
            const endLine = Math.min(lines.length - 1, i + contextLines);
            const context = lines.slice(startLine, endLine + 1).join("\n");

            // 找到匹配的列位置
            const matchIndex = line.search(searchPattern);

            results.push({
              type: "content",
              name: `匹配: ${matches[0]}`,
              file: relativePath,
              line: i + 1,
              column: matchIndex + 1,
              content: matches[0],
              context: context,
              kind: SymbolKind.String,
            });
          }
        }
      } catch (err) {
        // 忽略无法读取的文件
        continue;
      }
    }
  } catch (error) {
    console.error("内容搜索错误:", error);
  }

  return results;
}

function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
